import { RoleKeys, rolesDictionary } from '@/auth/enum/auth.enum';
import { AuthService } from '@/auth/service/auth.service';
import { FilterList } from '@/interfaces/person';
import { Manager } from '@/interfaces/requirement';
import { PersonService } from '@/services/person/person.service';
import { RequirementService } from '@/services/requirement/requirement.service';
import { AfterViewChecked, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-form-employee[isEmployee]',
  templateUrl: './form-employee.component.html',
  styleUrls: ['./form-employee.component.css'],
})
export class FormEmployeeComponent implements OnInit, AfterViewChecked {

  formSchema: FormGroup = new FormGroup({
    managerFormControl: new FormControl('', [Validators.required]),
    superintendentFormControl: new FormGroup({
      filterText: new FormControl(''),
      id: new FormControl(0, [Validators.required, Validators.min(1)]),
      mdmid: new FormControl('', [Validators.required]),
    }),
  });
  @Input() isEmployee: boolean = false;

  @Input() numberSection: string = ''
  @Input()
  set assetId(id: number | undefined) {
    this._assetId = id;
    if (id) {
      this.checkSIshouldBeRequired(id);
      this.getAllManagers(id);
    }
  }
  get assetId(): number | undefined {
    return this._assetId;
  }
  private _assetId: number | undefined;
  @Input() enabled: boolean = false;
  @Input() colorBoxes: 'default' | 'white' = 'default';
  @Input() showTitle: boolean = true;

  @Input() controlForm = new FormGroup({
    managerFormControl: new FormControl(''),
    superintendentFormControl: new FormGroup({
      filterText: new FormControl(''),
      id: new FormControl(0),
      mdmid: new FormControl(''),
    }),
  });

  get managerFormControl(): FormControl {
    return this.controlForm.controls.managerFormControl as FormControl;
  }
  get superintendentFormControl(): FormGroup {
    return this.controlForm.controls.superintendentFormControl as FormGroup;
  }
  get superintendentIdFormControl(): FormControl {
    const formGroup = this.controlForm.controls.superintendentFormControl as FormGroup;
    return formGroup.controls.id as FormControl;
  }

  listSuperintendentOptions: FilterList = [];
  loadingPersonOptions: boolean = false;

  managersDataByAsset: Manager[] = [];

  shouldRequireSI: boolean = false;
  previousAssetId: number = 0;

  constructor(
    private personService: PersonService,
    private requirementService: RequirementService,
    private auth: AuthService,
  ) { }

  ngOnInit(): void {

    const SI_ROLE = Number((<unknown>Object.keys(rolesDictionary) as RoleKeys[]).find((key) => rolesDictionary[key] === 'DLMS_EMPLOYEE_SI'));
    this.superintendentFormControl.controls.filterText.valueChanges
      .pipe(
        tap(() => {
          this.loadingPersonOptions = true;
        }),
        debounceTime(2000),
        switchMap((value) => {
          this.loadingPersonOptions = true;
          const iAmFullAccess = this.auth.userHaveRoleClean('DLMS_OWNER_MEL') || this.auth.userHaveRoleClean('DLMS_OWNER_SPC');

          return this.personService.getFilteredListRole(value, SI_ROLE, true, iAmFullAccess);
        }),
      )
      .subscribe({
        next: (options) => {
          this.loadingPersonOptions = false;
          this.listSuperintendentOptions = options;
        },
      });
  }

  ngAfterViewChecked(): void {
    if (this.assetId && this.previousAssetId !== this.assetId) {
      this.previousAssetId = this.assetId;
      this.checkSIshouldBeRequired(this.assetId);
    }
  }

  checkSIshouldBeRequired(assetId: number): void {
    // Si es SPC y la persona es un contratista no pedir el SI
    this.shouldRequireSI = !(+assetId === 2 && !this.isEmployee);

    const customFormSchema = new FormGroup({});
    this.setFormGroupSchema(customFormSchema, this.formSchema);
    const formControlSI = customFormSchema.controls.superintendentFormControl as FormGroup;

    if (!this.shouldRequireSI) {
      formControlSI.controls.id.clearValidators();
      formControlSI.controls.id.clearAsyncValidators();
      formControlSI.controls.mdmid.clearValidators();
      formControlSI.controls.mdmid.clearAsyncValidators();
    }

    this.setFormGroupSchema(this.controlForm, customFormSchema);

    (this.controlForm.controls.superintendentFormControl as FormGroup).controls.mdmid.updateValueAndValidity();
    (this.controlForm.controls.superintendentFormControl as FormGroup).controls.id.updateValueAndValidity();

  }

  onSearchSuperintendentTextChange(text: string) {
    this.superintendentFormControl.patchValue({ filterText: text });
  }

  onSearchSuperintendentSelected(id: string) {
    const superintendent = this.listSuperintendentOptions.find(
      (option: any) => +option.id === +id,
    );
    this.superintendentFormControl.patchValue({
      id: +id,
      mdmid: superintendent?.mdmid,
    });
  }

  getAllManagers(assetId: number) {
    this.managersDataByAsset = [];
    this.requirementService
      .getAllManagers(assetId)
      .subscribe((managers) => {
        this.managersDataByAsset = managers;
      });
  }

  getRequestManagerTooltip(assetId?: number): string {
    let message = 'Equipo que verificará la veracidad de los documentos y las fechas indicadas.';
    if (!assetId) {
      return message;
    }
    if (+assetId === 2) {
      message = '1.- En caso de ser del equipo de empleados BHP de Spence, o en caso de que su acreditación';
      message += '(contratista) se haya realizado con el equipo de Acredita, usted debe realizar la solicitud ';
      message += 'de licencia con Acredita y marcar Operaciones.';
      message += '\n2.- En caso de que su acreditación se haya realizado con el equipo de Proyectos (Ángelo Figueroa, para';
      message += ' proyectos STWM, SGO, SCU, etc), usted debe marcar Proyectos.';
    }

    return message;
  }

  private setFormGroupSchema(controlForm: FormGroup, formSchema: FormGroup) {
    const groupValidators = formSchema.validator;
    controlForm.setValidators(groupValidators);

    const controlSchemaKeys = Object.keys(formSchema.controls);
    controlSchemaKeys.map((key) => {
      const schema = formSchema.controls[key];
      let control = controlForm.controls[key];
      if (!control) {
        controlForm.addControl(key, this.getControlInstance(schema));
        control = controlForm.controls[key];
      }
      if (control instanceof FormGroup) {
        this.setFormGroupSchema(control, schema as FormGroup);
      }
      const validators = schema.validator;
      control.setValidators(validators);
    });
  }

  private getControlInstance(abstractControl: AbstractControl) {
    if (abstractControl instanceof FormArray) {
      return new FormArray([]);
    }
    if (abstractControl instanceof FormGroup) {
      return new FormGroup({});
    }
    return new FormControl();
  }
}
